/* Reya immersive gallery — crossfade stage + thumb rail (desktop), snap carousel (mobile),
   hover lens zoom, fullscreen lightbox with pinch / drag / wheel zoom and swipe.
   Hooks into Dawn's <media-gallery>.setActiveMedia() for variant media switching. */
(() => {
  if (customElements.get('rr-gallery')) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const pad = (n) => (n < 10 ? '0' : '') + n;

  class RRGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.mql = window.matchMedia('(min-width: 750px)');
      this.stage = this.querySelector('[data-rr-stage]');
      this.slides = Array.from(this.querySelectorAll('.rr-gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-rr-thumb]'));
      this.dots = Array.from(this.querySelectorAll('.rr-gallery__dot'));
      this.current = this.querySelector('[data-rr-current]');
      this.lens = this.querySelector('[data-rr-lens]');
      this.status = this.closest('media-gallery')?.querySelector('[role="status"]');
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      const host = this.closest('media-gallery');
      if (host) host.rrGallery = this;

      this.thumbs.forEach((b) => b.addEventListener('click', () => this.goTo(+b.dataset.rrThumb)));
      this.querySelector('[data-rr-gprev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-rr-gnext]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.querySelectorAll('[data-rr-goto]').forEach((b) =>
        b.addEventListener('click', () => this.goTo(+b.dataset.rrGoto))
      );
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      });

      // Mobile: sync index with native scroll-snap
      let raf;
      this.stage.addEventListener(
        'scroll',
        () => {
          if (this.mql.matches) return;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
            if (i !== this.index) this.setActive(i, false);
          });
        },
        { passive: true }
      );
      this.mql.addEventListener?.('change', () => this.goTo(this.index, false));

      // Lightbox
      this.querySelectorAll('[data-rr-open]').forEach((b) =>
        b.addEventListener('click', () => this.openLightbox(+b.dataset.rrOpen))
      );
      if (this.dataset.lens === 'true' && this.lens) this.initLens();

      // Initial position + first-load scan
      if (this.index > 0 && !this.mql.matches) this.stage.scrollLeft = this.slides[this.index].offsetLeft;
      this.scan();
    }

    /* ---------- navigation ---------- */
    goToMediaId(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === mediaId);
      if (i > -1) this.goTo(i);
    }

    goTo(i, smooth = true) {
      const n = this.slides.length;
      if (!n) return;
      i = (i + n) % n;
      this.setActive(i, true);
      if (!this.mql.matches) {
        this.stage.scrollTo({ left: this.slides[i].offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      }
    }

    setActive(i, announce) {
      const changed = i !== this.index;
      this.index = i;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === i);
        s.setAttribute('aria-hidden', k === i ? 'false' : this.mql.matches ? 'true' : 'false');
      });
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === i);
        if (k === i) {
          t.setAttribute('aria-current', 'true');
          const rail = t.closest('[data-rr-rail]');
          if (rail) {
            const vertical = rail.scrollHeight > rail.clientHeight + 2;
            const r = t.getBoundingClientRect();
            const rr = rail.getBoundingClientRect();
            if (vertical && (r.top < rr.top || r.bottom > rr.bottom)) rail.scrollBy({ top: r.top - rr.top - 12, behavior: 'smooth' });
            if (!vertical && (r.left < rr.left || r.right > rr.right)) rail.scrollBy({ left: r.left - rr.left - 12, behavior: 'smooth' });
          }
        } else t.removeAttribute('aria-current');
      });
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
      if (this.current) this.current.textContent = pad(i + 1);
      if (changed) {
        window.pauseAllMedia && window.pauseAllMedia();
        this.scan();
        if (announce && this.status) this.status.textContent = `${i + 1} / ${this.slides.length}`;
      }
    }

    scan() {
      if (this.dataset.scan !== 'true' || reduce) return;
      this.classList.remove('is-scanning');
      void this.offsetWidth;
      this.classList.add('is-scanning');
      clearTimeout(this._scanT);
      this._scanT = setTimeout(() => this.classList.remove('is-scanning'), 1500);
    }

    /* ---------- hover lens ---------- */
    initLens() {
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      const zoom = 2.5;
      const lens = this.lens;
      this.slides.forEach((slide) => {
        const media = slide.querySelector('[data-rr-zoom-src]');
        if (!media) return;
        const img = media.querySelector('img');
        let loaded = false;
        const move = (e) => {
          if (!fine.matches || !this.mql.matches) return;
          const r = img.getBoundingClientRect();
          const vr = this.querySelector('.rr-gallery__viewport').getBoundingClientRect();
          const x = e.clientX - r.left;
          const y = e.clientY - r.top;
          if (x < 0 || y < 0 || x > r.width || y > r.height) return this.hideLens();
          const hi = media.dataset.rrZoomSrc;
          if (lens.dataset.for !== hi) {
            lens.dataset.for = hi;
            lens.style.backgroundImage = `url("${loaded ? hi : img.currentSrc || img.src}")`;
            if (!loaded) {
              const pre = new Image();
              pre.onload = () => {
                loaded = true;
                if (lens.dataset.for === hi) lens.style.backgroundImage = `url("${hi}")`;
              };
              pre.src = hi; // 2400w loaded lazily, on first hover only
            }
          }
          const lw = lens.offsetWidth / 2;
          lens.style.backgroundSize = `${r.width * zoom}px ${r.height * zoom}px`;
          lens.style.backgroundPosition = `${-(x * zoom - lw)}px ${-(y * zoom - lw)}px`;
          lens.style.transform = `translate3d(${e.clientX - vr.left - lw}px, ${e.clientY - vr.top - lw}px, 0)`;
          lens.classList.add('is-visible');
        };
        media.addEventListener('pointermove', move);
        media.addEventListener('pointerleave', () => this.hideLens());
      });
    }
    hideLens() {
      this.lens && this.lens.classList.remove('is-visible');
    }

    /* ---------- lightbox ---------- */
    get images() {
      return this.slides.filter((s) => s.querySelector('[data-rr-full-srcset]'));
    }

    openLightbox(slideIndex) {
      const lb = this.querySelector('[data-rr-lightbox]') || this._lb;
      if (!lb) return;
      if (!this._lb) {
        this._lb = lb;
        document.body.appendChild(lb); // escape transformed ancestors
        this.bindLightbox(lb);
      }
      this._opener = document.activeElement;
      const imgs = this.images;
      this.lbIndex = Math.max(0, imgs.indexOf(this.slides[slideIndex]));
      lb.hidden = false;
      document.documentElement.classList.add('rr-lock');
      requestAnimationFrame(() => lb.classList.add('is-open'));
      this.renderLightbox();
      lb.querySelector('[data-rr-lb-close]').focus();
    }

    closeLightbox() {
      const lb = this._lb;
      if (!lb) return;
      lb.classList.remove('is-open');
      document.documentElement.classList.remove('rr-lock');
      setTimeout(() => (lb.hidden = true), 300);
      this._opener && this._opener.focus && this._opener.focus();
      const slide = this.images[this.lbIndex];
      if (slide) this.goTo(this.slides.indexOf(slide), false);
    }

    renderLightbox(dir = 0) {
      const lb = this._lb;
      const imgs = this.images;
      const media = imgs[this.lbIndex].querySelector('[data-rr-full-srcset]');
      const img = lb.querySelector('[data-rr-lb-img]');
      this.resetZoom();
      img.classList.remove('is-in');
      img.style.setProperty('--rr-dir', dir);
      img.onload = () => requestAnimationFrame(() => img.classList.add('is-in'));
      img.sizes = '100vw';
      img.srcset = media.dataset.rrFullSrcset;
      img.alt = media.dataset.rrAlt || '';
      if (img.complete && img.naturalWidth) requestAnimationFrame(() => img.classList.add('is-in'));
      lb.querySelector('[data-rr-lb-counter]').textContent = `${pad(this.lbIndex + 1)} / ${pad(imgs.length)}`;
      const multi = imgs.length > 1;
      lb.querySelectorAll('[data-rr-lb-prev],[data-rr-lb-next]').forEach((b) => (b.hidden = !multi));
    }

    stepLightbox(d) {
      const n = this.images.length;
      if (n < 2) return;
      this.lbIndex = (this.lbIndex + d + n) % n;
      this.renderLightbox(d);
    }

    resetZoom() {
      this.z = { s: 1, x: 0, y: 0 };
      this.applyZoom();
    }

    applyZoom() {
      const img = this._lb && this._lb.querySelector('[data-rr-lb-img]');
      if (!img) return;
      const { s, x, y } = this.z;
      img.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s})`;
      this._lb.classList.toggle('is-zoomed', s > 1.01);
    }

    clampZoom() {
      const img = this._lb.querySelector('[data-rr-lb-img]');
      const s = (this.z.s = Math.min(4, Math.max(1, this.z.s)));
      const mx = (img.offsetWidth * (s - 1)) / 2;
      const my = (img.offsetHeight * (s - 1)) / 2;
      this.z.x = Math.min(mx, Math.max(-mx, this.z.x));
      this.z.y = Math.min(my, Math.max(-my, this.z.y));
      if (s === 1) this.z.x = this.z.y = 0;
    }

    bindLightbox(lb) {
      const stage = lb.querySelector('[data-rr-lb-stage]');
      const img = lb.querySelector('[data-rr-lb-img]');
      lb.querySelector('[data-rr-lb-close]').addEventListener('click', () => this.closeLightbox());
      lb.querySelector('[data-rr-lb-prev]').addEventListener('click', () => this.stepLightbox(-1));
      lb.querySelector('[data-rr-lb-next]').addEventListener('click', () => this.stepLightbox(1));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeLightbox();
        if (e.key === 'ArrowRight') this.stepLightbox(1);
        if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        if (e.key === 'Tab') {
          const f = Array.from(lb.querySelectorAll('button:not([hidden])'));
          const first = f[0];
          const last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });

      const pts = new Map();
      let start = null;
      let pinch = null;
      let moved = false;
      let lastTap = 0;

      stage.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button')) return;
        stage.setPointerCapture(e.pointerId);
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        moved = false;
        if (pts.size === 1) start = { x: e.clientX, y: e.clientY, zx: this.z.x, zy: this.z.y, t: Date.now() };
        if (pts.size === 2) {
          const [a, b] = [...pts.values()];
          pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), s: this.z.s };
        }
      });
      stage.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size === 2 && pinch) {
          const [a, b] = [...pts.values()];
          this.z.s = pinch.s * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.d);
          this.clampZoom();
          this.applyZoom();
          moved = true;
          return;
        }
        if (!start) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        if (this.z.s > 1) {
          this.z.x = start.zx + dx;
          this.z.y = start.zy + dy;
          this.clampZoom();
          this.applyZoom();
        } else {
          img.style.transform = `translate3d(${dx}px, 0, 0)`;
        }
      });
      const end = (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = null;
        if (pts.size || !start) return;
        const dx = e.clientX - start.x;
        if (this.z.s <= 1.01 && moved && Math.abs(dx) > 60) {
          this.stepLightbox(dx < 0 ? 1 : -1);
        } else if (!moved) {
          // tap / click: toggle zoom at point (double-tap on touch)
          const now = Date.now();
          const touch = e.pointerType === 'touch';
          if (!touch || now - lastTap < 320) {
            if (this.z.s > 1) this.resetZoom();
            else {
              const r = img.getBoundingClientRect();
              this.z.s = 2.5;
              this.z.x = (r.left + r.width / 2 - e.clientX) * 1.5;
              this.z.y = (r.top + r.height / 2 - e.clientY) * 1.5;
              this.clampZoom();
              this.applyZoom();
            }
            lastTap = 0;
          } else lastTap = now;
        } else if (this.z.s <= 1.01) this.applyZoom();
        start = null;
      };
      stage.addEventListener('pointerup', end);
      stage.addEventListener('pointercancel', end);
      stage.addEventListener(
        'wheel',
        (e) => {
          e.preventDefault();
          this.z.s *= e.deltaY < 0 ? 1.12 : 0.89;
          this.clampZoom();
          this.applyZoom();
        },
        { passive: false }
      );
    }

    disconnectedCallback() {
      if (this._lb && this._lb.parentNode === document.body) this._lb.remove();
      document.documentElement.classList.remove('rr-lock');
    }
  }

  customElements.define('rr-gallery', RRGallery);
})();
